import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useSearchParams } from 'react-router-dom';
import { captureException } from '@sentry/react';
import { useClientUser } from '@/hooks';
import { CLIENT_BY_ID } from '@/graphql/queries/users';
import { useDrillIn } from '@/components/DrillInPage';
import { post } from '@/utilities/onwardClient';
import { INVITE_PARTNER } from '@/constants/apiRoutes';
import { GET_SHIPPER_INTEGRATION_STATUS } from './queries';
import { CONNECT_SHIPPING_PARTNER, DELETE_SHIPPING_PARTNER } from './mutations';
import { getSetupStatusByShipper, NO_SETUP } from './utils';

/** The query param holding the partnership of the open shipper profile. */
export const SHIPPER_PARAM = 'shipper';

// `getSetup` is null while loading or after a failed read, so a row never shows a status that wasn't checked.
const useShipperSetupStatus = (carrierId, shipperIds) => {
    const { data, loading, error } = useQuery(GET_SHIPPER_INTEGRATION_STATUS, {
        variables: { carrier_id: carrierId, shipper_ids: shipperIds },
        skip: !carrierId || !shipperIds.length,
        onError: (e) => captureException(e),
    });

    const setupByShipper = useMemo(() => getSetupStatusByShipper(data || {}), [data]);
    const known = !loading && !error;

    const getSetup = useCallback(
        (shipperId) => (known ? setupByShipper[shipperId] || NO_SETUP : null),
        [known, setupByShipper]
    );

    return { getSetup };
};

const inviteMessage = { severity: 'success', message: 'Shipping partner invited via email!' };
const inviteError = { severity: 'error', message: 'Error inviting partner. Please try again' };

/**
 * State and actions for the carrier's Shippers tab: the partner list, the selected shipper, and
 * connect / resend invite / delete.
 */
export const useShippersTab = () => {
    const { user_id, shipping_partners, businessName } = useClientUser();
    const [notification, setNotification] = useState({});
    const [addingShipper, setAddingShipper] = useState(false);
    // The open profile is in the query string (`?shipper=`), so back, refresh and links keep it.
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const drillIn = useDrillIn();
    const selectedId = searchParams.get(SHIPPER_PARAM);
    const [pendingDelete, setPendingDelete] = useState(null);

    const partners = shipping_partners || [];
    const shipperIds = useMemo(() => partners.map((p) => p.shipper_id), [partners]);
    const { getSetup } = useShipperSetupStatus(user_id, shipperIds);

    const selectedShipper = useMemo(
        () => partners.find((partner) => partner.partnership_id === selectedId) || null,
        [partners, selectedId]
    );

    const shipperLabels = useMemo(
        () => [...new Set(partners.flatMap((partner) => partner.shipper_label || []))],
        [partners]
    );

    const [connectShipper, { loading: connectLoading }] = useMutation(CONNECT_SHIPPING_PARTNER, {
        refetchQueries: [CLIENT_BY_ID],
        awaitRefetchQueries: true,
        onCompleted: ({ insert_partnerships_one }) => {
            setNotification({
                severity: 'success',
                message: `Successfully connected to ${insert_partnerships_one.shipper.business_name}!`,
            });
        },
        onError: (e) => {
            // Every name the pair's uniqueness has had: partnerships_unique_idx before the ic_entity
            // migration, then partnerships_pairing_unique_idx, plus partnerships_shipper_id_carrier_id_key,
            // which Postgres reports instead when it checks that index first (after a dump restore).
            if (
                e?.message?.includes('partnerships_unique_idx') ||
                e?.message?.includes('partnerships_pairing_unique_idx') ||
                e?.message?.includes('partnerships_shipper_id_carrier_id_key')
            ) {
                setNotification({ severity: 'warning', message: 'You have already connected to this shipper' });
                return;
            }
            console.error(e);
            captureException(e);
            setNotification({ severity: 'error', message: 'Error connecting to shipper' });
        },
    });

    const [deleteShipper, { loading: deleteLoading }] = useMutation(DELETE_SHIPPING_PARTNER, {
        onCompleted: () => setNotification({ severity: 'success', message: 'Shipper removed' }),
        update: (cache, { data: { delete_partnerships_by_pk } }) => {
            cache.evict(cache.identify(delete_partnerships_by_pk));
        },
        onError: (e) => {
            console.error(e);
            captureException(e);
            setNotification({ severity: 'error', message: 'Error removing shipper. Please try again' });
        },
    });

    const resendInvite = useCallback(
        (partnership) =>
            post(INVITE_PARTNER, {
                inviter_name: businessName,
                inviter_type: 'carrier',
                invitee_id: partnership.shipper_id,
                invitee_name: partnership.shipper?.business_name,
                invitee_email: partnership.shipper?.email,
                invitee_type: 'shipper',
            })
                .then(() => setNotification(inviteMessage))
                .catch((err) => {
                    console.error(err);
                    setNotification(inviteError);
                }),
        [businessName]
    );

    const addShipperCallbacks = useMemo(
        () => ({
            onClose: () => setAddingShipper(false),
            onConnect: (shipper_id, importedLogoDetails, shipperLabel) =>
                connectShipper({
                    variables: {
                        partnership: {
                            shipper_id,
                            carrier_id: user_id,
                            status: 'APPROVED',
                            ...importedLogoDetails,
                            shipper_label: shipperLabel || null,
                        },
                    },
                }),
        }),
        [connectShipper, user_id]
    );

    const confirmDelete = useCallback(() => {
        deleteShipper({ variables: { partnership_id: pendingDelete.partnership_id } });
        setPendingDelete(null);
    }, [deleteShipper, pendingDelete]);

    return {
        partners,
        getSetup,
        shipperLabels,
        selectedShipper,
        notification,
        setNotification,
        loading: connectLoading || deleteLoading,
        addingShipper,
        openAddShipper: () => setAddingShipper(true),
        addShipperCallbacks,
        selectShipper: (partnership) => drillIn(`${pathname}?${SHIPPER_PARAM}=${partnership.partnership_id}`),
        resendInvite,
        pendingDelete,
        requestDelete: setPendingDelete,
        cancelDelete: () => setPendingDelete(null),
        confirmDelete,
    };
};
